import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Eye, ChevronUp, ChevronDown } from 'lucide-react';

export interface TableColumn<T> {
  key: keyof T | string;
  title: React.ReactNode;
  width?: string;
  sortable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, record: T) => React.ReactNode;
}

interface DataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;

  // Tri
  onSort?: (key: string) => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';

  // Actions prédéfinies (rétrocompatibilité)
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  onView?: (record: T) => void;

  // Nouvelles fonctionnalités
  onRowDoubleClick?: (record: T) => void;
  onRowClick?: (record: T) => void;
  rowClassName?: string | ((record: T) => string);

  // Sélection par checkbox
  selectable?: boolean;
  selectedRows?: T[];
  onSelectionChange?: (selectedRows: T[]) => void;

  // Messages
  emptyMessage?: string;

  // Styles
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  onSort,
  sortKey,
  sortDirection,
  onEdit,
  onDelete,
  onView,
  onRowDoubleClick,
  onRowClick,
  rowClassName,
  selectable = false,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  emptyMessage = 'Aucune donnée disponible',
  className = ''
}: DataTableProps<T>) {
  // Gestion de la sélection interne si non contrôlée
  const [internalSelectedRows, setInternalSelectedRows] = useState<T[]>([]);
  const selectedRows = externalSelectedRows !== undefined ? externalSelectedRows : internalSelectedRows;

  // Réinitialiser la sélection quand les données changent
  useEffect(() => {
    if (!externalSelectedRows) {
      setInternalSelectedRows([]);
    }
  }, [data, externalSelectedRows]);

  const handleSelectionChange = (newSelection: T[]) => {
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    } else {
      setInternalSelectedRows(newSelection);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleSelectionChange([...data]);
    } else {
      handleSelectionChange([]);
    }
  };

  const handleSelectRow = (row: T, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.checked) {
      handleSelectionChange([...selectedRows, row]);
    } else {
      handleSelectionChange(selectedRows.filter(r => r.id !== row.id));
    }
  };

  const isRowSelected = (row: T): boolean => {
    return selectedRows.some(r => r.id === row.id);
  };

  const handleRowDoubleClick = (item: T, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onRowDoubleClick) {
      onRowDoubleClick(item);
    }
  };

  const handleRowClick = (item: T, event: React.MouseEvent) => {
    if (onRowClick) {
      event.preventDefault();
      event.stopPropagation();
      onRowClick(item);
    }
  };

  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  const getRowClassName = (item: T): string => {
    let classes = 'hover:bg-gray-50 transition-colors';

    if (onRowDoubleClick || onRowClick) {
      classes += ' cursor-pointer';
    }

    if (isRowSelected(item)) {
      classes += ' bg-blue-50';
    }

    if (typeof rowClassName === 'function') {
      classes += ` ${rowClassName(item)}`;
    } else if (rowClassName) {
      classes += ` ${rowClassName}`;
    }

    return classes;
  };

  // Détermine si nous devons afficher une colonne d'actions
  const hasActions = onEdit || onDelete || onView;

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow border border-gray-200 ${className}`}>
        <div className="p-8 text-center">
          <div className="animate-pulse">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow border border-gray-200 overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th className="px-6 py-3 text-left" style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable && onSort ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && onSort ? handleSort(String(column.key)) : undefined}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.title}</span>
                    {column.sortable && onSort && (
                      <div className="flex flex-col">
                        <ChevronUp
                          className={`h-3 w-3 ${
                            sortKey === column.key && sortDirection === 'asc'
                              ? 'text-blue-600'
                              : 'text-gray-400'
                          }`}
                        />
                        <ChevronDown
                          className={`h-3 w-3 ${
                            sortKey === column.key && sortDirection === 'desc'
                              ? 'text-blue-600'
                              : 'text-gray-400'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {hasActions && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(!data || data.length === 0) ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0) + (selectable ? 1 : 0)}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              (data || []).map((item, index) => (
                <tr
                  key={item.id || index}
                  className={getRowClassName(item)}
                  onDoubleClick={(event) => handleRowDoubleClick(item, event)}
                  onClick={(event) => handleRowClick(item, event)}
                  title={onRowDoubleClick ? "Double-cliquez pour ouvrir" : undefined}
                >
                  {selectable && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isRowSelected(item)}
                        onChange={(e) => handleSelectRow(item, e)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={String(column.key)} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.render
                        ? column.render(item[column.key as keyof T], item)
                        : item[column.key as keyof T]
                      }
                    </td>
                  ))}
                  {hasActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {onView && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(item);
                            }}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Voir"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(item);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(item);
                            }}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;